import React, { useState, useEffect } from 'react';
import CustomerPage from '../../homeDashboard/templates/CustomerPage';
import DriverPage from '../../driverDashboard/template/DriverPage';
import { Helmet } from "react-helmet";
import { PulseLoader } from 'react-spinners';
import { FaBell, FaBellSlash, FaChevronRight } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";
import { fetchNotifications, markNotificationAsRead } from "../../Services/ApiCallsServices/NotificationsApi";
import { useAuth } from "../../context/AuthContext";
import { useDispatch } from 'react-redux';
import { setSelectedTripRef } from "../../Reducers/notificationSlice";

const Notification = () => {
    const navigate = useNavigate();
    const { auth } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const name = auth.user?.first_name || '';
    const email = auth.user?.email || '';
    const role = auth.user?.role || '';
    const dispatch = useDispatch();

    const Container = (role === 'driver' || role === 'admin') ? DriverPage : CustomerPage;

    useEffect(() => {
        if (!email) return;

        const loadNotifications = async () => {
            try {
                const data = await fetchNotifications(email);


                const rawNotifications = Array.isArray(data)
                    ? data
                    : data?.notifications || [];

                setNotifications(rawNotifications);
            } catch (error) {
                console.error("Error fetching notifications:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadNotifications();
    }, [email]);

    const handleNotificationClick = async (notificationId, tripReference) => {
        if (!tripReference) {
            console.error("Trip reference is undefined");
            return;
        }

        try {
            await markNotificationAsRead(notificationId, email);

            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId ? { ...n, is_read: true } : n
                )
            );

            dispatch(setSelectedTripRef(tripReference));

            navigate(role === 'driver' ? '/trip-progress' : '/customer-trip');
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    const formatCreatedAt = createdAt => {
        if (!createdAt) return '';

        const date = new Date(createdAt);
        const diffHrs = (Date.now() - date) / 36e5;

        return diffHrs < 24
            ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
            : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

     return (
        <Container>
            <Helmet>
                <title>Notifications | Storage Sprint</title>
            </Helmet>
            
            <div className="min-h-screen bg-gray-50 p-4 md:p-8">
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                            <span className="text-yellow-500">Your</span> Notifications
                        </h1>
                        <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                            {notifications.filter(n => !n.is_read).length} Unread
                        </div>
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <PulseLoader color="#FFD700" size={15} />
                        </div>
                    ) : notifications.length === 0 ? (
                        /* Empty State */
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            <FaBellSlash className="mx-auto text-gray-400 text-4xl mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications yet</h3>
                            <p className="text-gray-500">We'll notify you when something arrives</p>
                        </div>
                    ) : (
                        /* Notifications List */
                        <div className="space-y-3">
                            {notifications.map(notif => (
                                <div
                                    key={notif.id}
                                    onClick={() => handleNotificationClick(notif.id, notif.trip_reference)}
                                    className={`bg-white rounded-lg shadow-sm p-4 border-l-4 ${
                                        notif.is_read 
                                            ? 'border-transparent hover:border-gray-200' 
                                            : 'border-yellow-500'
                                    } transition-all cursor-pointer hover:shadow-md`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center mb-1">
                                                <FaBell className={`mr-2 ${
                                                    notif.is_read ? 'text-gray-400' : 'text-yellow-500'
                                                }`} />
                                                <span className="font-medium text-gray-800">
                                                    {notif.trip_reference || 'New Update'}
                                                </span>
                                            </div>
                                            <p className="text-gray-600 mb-2">{notif.message}</p>
                                            <div className="flex items-center text-sm text-gray-500">
                                                <span>Hi {name},</span>
                                                <span className="mx-2">•</span>
                                                <span>{formatCreatedAt(notif.created_at)}</span>
                                            </div>
                                        </div>
                                        <FaChevronRight className="text-gray-400 ml-2 mt-1" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default Notification;